import { RefObject, useEffect, useRef } from "react";

interface UseInfiniteScrollOptions {
  sentinelRef: RefObject<HTMLDivElement | null>;
  isActive: boolean;
  isLoading: boolean;
  hasMoreItems: boolean;
  onLoadMore: () => void;
}

export const useInfiniteScroll = ({
  sentinelRef,
  isActive,
  isLoading,
  hasMoreItems,
  onLoadMore,
}: UseInfiniteScrollOptions) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!isActive || isLoading || !hasMoreItems) return;

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      { threshold: 0.5 }
    );

    if (sentinelRef.current) {
      observer.current.observe(sentinelRef.current);
    }

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [isActive, isLoading, hasMoreItems, sentinelRef]);
};
